import React, { FC, useMemo, useState } from "react";
import { useAuth } from "../../../providers/AuthProvider";
import { Row, Col, Card } from "react-bootstrap";
import { BvIcon } from "bevi-icon";
import Skeleton from "react-loading-skeleton";
// eslint-disable-next-line import/no-unresolved
import "react-loading-skeleton/dist/skeleton.css";
// eslint-disable-next-line import/no-unresolved
import MasterPage from "../../MasterPage/MasterPage";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import { useAIUsageDashboard } from "../../../providers/Dashboards/AIUsageDashboardProvider";
import { useSidebarFilter } from "../../../providers/SidebarFilterProvider";
import SidebarAIUsageDashboardFilter from "../../../components/Dashboard/SidebarAIUsageDashboardFilter/SidebarAIUsageDashboardFilter";
import AppPagination from "../../../components/AppPagination/AppPagination";
import { PagesPaginateModel } from "../../../models/Paginate/PagesPaginate";
// eslint-disable-next-line import/no-unresolved
import "./AIUsageDashboard.scss";

const COLORS = ["#00002E", "#000094", "#0000C7", "#0000FA", "#2E2EFF", "#6161FF", "#9494FF", "#C7C7FF", "#E5E5FF", "#F0F0FF"];
const ITEMS_PER_PAGE = 12;
const LOCALE = "pt-BR";

const capitalize = (text?: string): string => {
  if (!text) return "";
  return text
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatNumber = (value: number) => new Intl.NumberFormat(LOCALE).format(value);

interface KPICardProps {
  icon?: string;
  iconImage?: string;
  label: string;
  value: string | number;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  isLoading?: boolean;
  helpText?: string;
  showInfoIcon?: boolean;
}

const KPICard: FC<KPICardProps> = ({
  icon,
  iconImage,
  label,
  value,
  trend,
  isLoading = false,
  helpText,
  showInfoIcon = false,
}) => {
  if (isLoading) {
    return (
      <Card className="kpi-card">
        <Card.Body>
          <Skeleton height={40} width="80%" />
          <Skeleton height={30} width="60%" className="mt-2" />
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="kpi-card">
      <div className="kpi-card__header">
        <div className="kpi-card__header-left">
          {iconImage && (
            <div className="kpi-card__icon">
              <img src={iconImage} alt={label} />
            </div>
          )}
          {icon && !iconImage && (
            <div className="kpi-card__icon">
              <BvIcon name={icon} color="#444688" width={28} height={28} />
            </div>
          )}
        </div>

        <div className="kpi-card__header-middle">
          <span className="kpi-card__label">{label}</span>
        </div>

        <div className="kpi-card__header-right">
          {trend && (
            <div className="kpi-card__trend">
              <div className={`kpi-card__badge ${trend.isPositive ? "positive" : "negative"}`}>
                <BvIcon
                  name={trend.isPositive ? "arrow-up" : "arrow-down"}
                  variant="solid"
                  color="#444688"
                  className={`kpi-card__badge-icon ${trend.isPositive ? "" : "rotated"}`}
                />
                <span>{Math.abs(trend.value)}%</span>
              </div>
            </div>
          )}

          {(helpText || showInfoIcon) && (
            <div className="kpi-card__help">
              <div className="kpi-card__help-icon" data-tooltip={helpText}>
                <BvIcon name={showInfoIcon ? "info" : "question-circle"} variant={showInfoIcon ? "light" : "solid"} width={showInfoIcon ? 24 : undefined} height={showInfoIcon ? 24 : undefined} color="#444688" />
              </div>
            </div>
          )}
        </div>
      </div>

      <Card.Body className="kpi-card__body">
        <div className="kpi-card__value">{value}</div>
      </Card.Body>
    </Card>
  );
};

interface DashboardChartProps {
  title: string;
  subtitle?: string;
  icon?: string;
  iconImage?: string;
  isLoading?: boolean;
  children: React.ReactNode;
  lg?: number;
  md?: number;
  sm?: number;
}

const DashboardChart: FC<DashboardChartProps> = ({
  title,
  subtitle,
  icon,
  iconImage,
  isLoading = false,
  children,
  lg = 6,
  md = 12,
  sm = 12,
}) => {
  return (
    <Col lg={lg} md={md} sm={sm}>
      <Card className="ai-usage-dashboard__chart">
        <Card.Header className="ai-usage-dashboard__chart-header">
          <div className="ai-usage-dashboard__chart-header-left">
            {iconImage && <img src={iconImage} alt={title} className="ai-usage-dashboard__chart-icon-image" />}
            {icon && !iconImage && <BvIcon name={icon} variant="solid" color="#444688" width={28} height={28} />}
          </div>
          <div className="ai-usage-dashboard__chart-header-middle">
            <h5 className="ai-usage-dashboard__chart-title">{title}</h5>
            {subtitle && <p className="ai-usage-dashboard__chart-subtitle">{subtitle}</p>}
          </div>
        </Card.Header>

        <Card.Body className="ai-usage-dashboard__chart-body">
          {isLoading ? (
            <div className="chart-container__loading">
              <Skeleton height={400} width="100%" />
            </div>
          ) : (
            children
          )}
        </Card.Body>
      </Card>
    </Col>
  );
};

interface Column {
  key: string;
  label: string;
  render?: (value: any, row: any) => React.ReactNode;
  align?: "left" | "center" | "right";
  width?: string;
}

interface DashboardTableProps {
  title: string;
  subtitle?: string;
  icon?: string;
  columns: Column[];
  data: any[];
  isLoading?: boolean;
  lg?: number;
  md?: number;
  sm?: number;
}

const DashboardTable: FC<DashboardTableProps> = ({
  title,
  subtitle,
  icon,
  columns,
  data,
  isLoading = false,
  lg = 6,
  md = 12,
  sm = 12,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = ITEMS_PER_PAGE;

  const totalItems = data?.length || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = data?.slice(startIndex, startIndex + itemsPerPage) || [];

  const pages: PagesPaginateModel = {
    current: currentPage,
    first: 1,
    last: totalPages,
    previous: currentPage > 1 ? currentPage - 1 : undefined,
    next: currentPage < totalPages ? currentPage + 1 : undefined,
    total: totalItems,
    perPage: itemsPerPage,
    position: {
      first: totalItems > 0 ? startIndex + 1 : 0,
      last: Math.min(startIndex + itemsPerPage, totalItems),
    },
  };

  const handlePaginate = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <Col lg={lg} md={md} sm={sm}>
      <Card className="ai-usage-dashboard__table">
        <Card.Header className="ai-usage-dashboard__table-header">
          <div className="ai-usage-dashboard__table-header-left">
            {icon && <BvIcon name={icon} variant="solid" color="#444688" width={28} height={28} />}
          </div>
          <div className="ai-usage-dashboard__table-header-middle">
            <h5 className="ai-usage-dashboard__table-title">{title}</h5>
            {subtitle && <p className="ai-usage-dashboard__table-subtitle">{subtitle}</p>}
          </div>
        </Card.Header>

        <Card.Body className="ai-usage-dashboard__table-body">
          {isLoading ? (
            <div className="chart-container__skeletons">
              <Skeleton count={5} height={40} className="mb-2" />
            </div>
          ) : (
            <>
              <div className="ai-usage-dashboard__table-wrapper">
                <table className="ai-usage-dashboard__table-content">
                  <thead className="ai-usage-dashboard__table-thead">
                    <tr>
                      {columns.map((col) => (
                        <th
                          key={col.key}
                          style={{ width: col.width }}
                          className={`text-${col.align || "left"}`}
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="ai-usage-dashboard__table-tbody">
                    {paginatedData && paginatedData.length > 0 ? (
                      paginatedData.map((row, idx) => (
                        <tr key={idx} className="ai-usage-dashboard__table-row">
                          {columns.map((col) => (
                            <td
                              key={`${idx}-${col.key}`}
                              className={`text-${col.align || "left"}`}
                            >
                              {col.render ? col.render(row[col.key], row) : row[col.key]}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={columns.length} className="ai-usage-dashboard__table-empty text-center">
                          Nenhum dado disponível
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {(data?.length || 0) > itemsPerPage && (
                <nav aria-label="Paginação" className="ai-usage-dashboard__table-pagination">
                  <AppPagination
                    pages={pages}
                    handlePaginate={handlePaginate}
                    position="bottom"
                  />
                </nav>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </Col>
  );
};

const AIUsageDashboard: FC = () => {
  const { isLoading: authLoading } = useAuth();
  const { kpis, collaborators, isLoadingKpis, isLoading } = useAIUsageDashboard();
  const { showSidebarFilter, setShowSidebarFilter } = useSidebarFilter();

  const dashboards = [
    { value: 'produtividade', label: 'Produtividade' },
    { value: 'ai-usage', label: 'Uso da IA' },
  ];

  const collaboratorsColumns = [
    {
      key: "collaborator_name",
      label: "Colaborador",
      width: "30%",
      render: (value: string) => capitalize(value),
    },
    {
      key: "total_positive_votes",
      label: "Positivo",
      width: "15%",
      align: "center" as const,
    },
    {
      key: "total_negative_votes",
      label: "Negativo",
      width: "15%",
      align: "center" as const,
    },
    {
      key: "total_interactions",
      label: "Interações",
      width: "15%",
      align: "center" as const,
    },
    {
      key: "assertiveness_rate",
      label: "Taxa Assertividade",
      width: "25%",
      align: "right" as const,
      render: (value: number) => value ? (value as number).toFixed(2) + "%" : "—",
    },
  ];

  const chartTotals = useMemo(() => {
    return {
      total_messages: kpis?.kpis?.total_messages || 0,
      total_votes: kpis?.kpis?.total_votes || 0,
      total_interactions: kpis?.kpis?.total_interactions || 0,
      avg_messages_per_chat: kpis?.kpis?.avg_messages_per_chat || 0,
      assertiveness_rate: kpis?.kpis?.assertiveness_rate || 0,
    };
  }, [kpis]);

  const topCollaborators = useMemo(() => {
    return (collaborators || [])
      .sort((a: any, b: any) => (b.total_interactions || 0) - (a.total_interactions || 0))
      .slice(0, 10);
  }, [collaborators]);

  const rankingChartTotal = useMemo(() => {
    return topCollaborators.reduce((sum: number, item: any) => sum + item.total_interactions, 0);
  }, [topCollaborators]);

  const collaboratorsTableData = useMemo(() => {
    return (collaborators || []).sort(
      (a: any, b: any) => (b.assertiveness_rate || 0) - (a.assertiveness_rate || 0)
    );
  }, [collaborators]);

  const RankingBarTooltip = (props: any) => {
    const { active, payload } = props;
    if (!active || !payload || payload.length === 0) return null;

    const data = payload[0].payload;

    return (
      <div className="materials-tooltip">
        <p className="materials-tooltip__item">
          <BvIcon name="person" variant="dark" size={1.2} color="#444688" />
          <span>{capitalize(data.collaborator_name)}</span>
        </p>
        <p className="materials-tooltip__item">
          <BvIcon name="questionAnswer" variant="dark" size={1.2} color="#444688" />
          <span>Interações: </span>
          <span style={{ color: "#6161ff", fontWeight: 600 }}>{formatNumber(data.total_interactions)}</span>
        </p>
      </div>
    );
  };

  const CustomBarLabel = (props: any) => {
    const { x, y, width, height, value, fill } = props;
    const percentage = ((value / rankingChartTotal) * 100).toFixed(0);
    const barCenterX = x + width / 2;
    const labelWidth = width * 0.75;
    const labelHeight = 24;
    const labelY = y + height - labelHeight - 4;
    const labelX = barCenterX - labelWidth / 2;

    const isLightColor = COLORS.indexOf(fill) >= 7;
    const tarjaColor = isLightColor ? "rgba(0, 0, 0, 0.16)" : "rgba(255, 255, 255, 0.16)";

    const tl = 2;
    const tr = 2;
    const br = 5;
    const bl = 5;

    const pathData = `
      M ${labelX + tl} ${labelY}
      L ${labelX + labelWidth - tr} ${labelY}
      Q ${labelX + labelWidth} ${labelY} ${labelX + labelWidth} ${labelY + tr}
      L ${labelX + labelWidth} ${labelY + labelHeight - br}
      Q ${labelX + labelWidth} ${labelY + labelHeight} ${labelX + labelWidth - br} ${labelY + labelHeight}
      L ${labelX + bl} ${labelY + labelHeight}
      Q ${labelX} ${labelY + labelHeight} ${labelX} ${labelY + labelHeight - bl}
      L ${labelX} ${labelY + tl}
      Q ${labelX} ${labelY} ${labelX + tl} ${labelY}
      Z
    `;

    return (
      <g>
        <path
          d={pathData}
          fill={tarjaColor}
        />
        <text
          x={barCenterX}
          y={labelY + labelHeight / 2}
          textAnchor="middle"
          dominantBaseline="central"
          fill="white"
          fontSize="11"
          fontWeight="600"
        >
          {percentage}%
        </text>
      </g>
    );
  };

  if (authLoading) {
    return <MasterPage />;
  }

  const renderContent = () => (
    <div className="ai-usage-dashboard">
      <div className="ai-usage-dashboard__header">
        <h5 className="ai-usage-dashboard__title">Uso da IA</h5>
        <div className="ai-usage-dashboard__controls">
          <div className="ai-usage-dashboard__select-wrapper">
            <select
              className="ai-usage-dashboard__dashboard-select"
              defaultValue="ai-usage"
              onChange={(e) => {
                if (e.target.value === 'produtividade') {
                  window.location.href = '/dashboards/productivity';
                }
              }}
            >
              {dashboards.map((dashboard) => (
                <option key={dashboard.value} value={dashboard.value}>
                  {dashboard.label}
                </option>
              ))}
            </select>
            <BvIcon
              name="chevronDown"
              color="#25CBDB"
              width={16}
              height={16}
              className="ai-usage-dashboard__select-icon"
            />
          </div>
          <button
            type="button"
            className="btn btn-filter-dark btn-sm mb-0"
            onClick={() => setShowSidebarFilter(!showSidebarFilter)}
          >
            <BvIcon
              name="filter"
              variant="light"
              width={16}
              height={16}
              className="me-2"
              color="#25CBDB"
            />
            <span>Filtrar</span>
          </button>
        </div>
      </div>

      <Row className="g-3 row--compact">
        <Col lg={6} md={12} sm={12}>
          <KPICard
            icon="questionAnswer"
            label="Total de Interações"
            value={formatNumber(chartTotals.total_interactions)}
            isLoading={isLoadingKpis}
            showInfoIcon
            helpText="Número total de interações"
          />
        </Col>
        <Col lg={6} md={12} sm={12}>
          <KPICard
            icon="sentimentSatisfied"
            label="Taxa de Satisfação"
            value={(chartTotals.assertiveness_rate as number).toFixed(2) + "%"}
            isLoading={isLoadingKpis}
            showInfoIcon
            helpText="Percentual de satisfação"
          />
        </Col>
      </Row>

      <Row className="g-3 row--extra-compact row--equal-height">
        <Col lg={4} md={12} sm={12}>
          <DashboardChart
            title="Top 10 Colaboradores (Interações)"
            subtitle="Ranking por interações"
            icon="leaderboard"
            isLoading={isLoading}
            lg={12}
          >
            <div className="ranking-card__container chart-container--medium">
              <ResponsiveContainer width="100%" height={400} minWidth={0}>
                <BarChart
                  data={topCollaborators}
                  margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
                  barCategoryGap={topCollaborators.length < 5 ? "40%" : "15%"}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#cccccc" vertical={false} />
                  <XAxis dataKey="collaborator_name" stroke="#cccccc" tick={false} />
                  <YAxis stroke="#cccccc" />
                  <Tooltip content={<RankingBarTooltip />} cursor={false} />
                  <Bar
                    dataKey="total_interactions"
                    fill="#6161ff"
                    radius={[8, 8, 8, 8]}
                    isAnimationActive={false}
                    label={<CustomBarLabel />}
                  >
                    {topCollaborators.map((_: any, index: number) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              <div className="ranking-card__legend-wrapper">
                <table className="ranking-card__table">
                  <thead className="ranking-card__thead">
                    <tr>
                      <th>Colaborador</th>
                      <th className="text-right">Interações</th>
                    </tr>
                  </thead>
                  <tbody className="ranking-card__tbody">
                    {topCollaborators.map((item: any, index: number) => (
                      <tr key={index}>
                        <td className="text-left">
                          <span className="ranking-card__color-box" style={{ backgroundColor: COLORS[index % COLORS.length] }}></span>
                          {capitalize(item.collaborator_name)}
                        </td>
                        <td className="text-right">{formatNumber(item.total_interactions || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </DashboardChart>
        </Col>

        <Col lg={8} md={12} sm={12}>
          <DashboardTable
            title="Histórico por Colaborador"
            subtitle="Desempenho completo de todos os colaboradores"
            icon="pendingActions"
            columns={collaboratorsColumns}
            data={collaboratorsTableData}
            isLoading={isLoading}
            lg={12}
          />
        </Col>
      </Row>
    </div>
  );

  return (
    <MasterPage title="Uso da IA" icon="fas fa-brain">
      <div className="AIUsageDashboard" data-testid="AIUsageDashboard">
        {renderContent()}
      </div>

      <SidebarAIUsageDashboardFilter />
    </MasterPage>
  );
};

export default AIUsageDashboard;
